<template>
  <div id="knowledge-base">
    <v-row>
      <v-col cols="12" md="3" lg="3" order="1" order-md="2" class="knowledge-base-summary-overlay">
        <v-card class="knowledge-base-summary" style="overflow-y: hidden">
          <div class="kb-questions py-1">
            <div
              v-for="subTitle in summary"
              :key="subTitle.title"
              class="kb-question d-flex align-start text-sm px-5 my-4 cursor-pointer"
            >
              <v-icon size="14" class="me-2 mt-1" color="secondary">
                {{ icons.mdiCheckboxBlankCircleOutline }}
              </v-icon>
              <span class="text--secondary"
                    @click="scrollToElement(subTitle.link)">{{ subTitle.title }}</span>
            </div>
          </div>
        </v-card>
      </v-col>
      <v-col cols="12" md="9" lg="9" order="1" order-md="2">
        <v-card>
          <v-card-text>
            <h2>Getting Started with Merchanto Prevent API</h2>
          </v-card-text>
          <v-card-text>This section walks you through the steps required to integrate with the Merchanto Prevent API. It covers endpoint setup, authentication, response requirements, testing, and monitoring.</v-card-text>
          <v-divider></v-divider>

          <v-card-title id="environment-setup">1. Environment setup:</v-card-title>
          <v-card-text>
            To integrate with Visa Order Insight (OI) and Mastercard Clarity, merchants must provide dedicated API endpoints.
          </v-card-text>
          <v-card-text>
            <b>Required endpoints (examples):</b>
            <ul>
              <li>
                <b>Visa Orders endpoint</b>
                <br/>
                <code>POST /api/prevent/visa/orders</code>
                <br>
                Used for receiving Visa OI lookup requests.
              </li>
              <li>
                <b>Mastercard Orders endpoint</b>
                <br/>
                <code>POST /api/prevent/mastercard/orders</code>
                <br/>
                Used for receiving Mastercard Clarity lookup requests.
              </li>
              <li>
                <b>Notification endpoints (optional)</b>
                <br/>
                <code>POST /api/prevent/{visa|mastercard}/notifications</code>
                <br/>
                Used for receiving dispute-status notifications from Merchanto.
                <br/>
                If you don’t need notifications, these endpoints may be skipped.
              </li>
            </ul>
          </v-card-text>
          <v-card-text>
            <b>Important</b>: The endpoint paths above are <b>examples</b>. You can provide your own preferred endpoints
            (e.g., <code>/callbacks/visa/orders</code> or <code>/merchant/prevent/mc</code>) as long as they:
          </v-card-text>
          <v-card-text>
            <ul>
              <li>Accept <b>HTTP POST</b> requests.</li>
              <li>Do not include query parameters (e.g., <code>?id=...</code>).</li>
              <li>Are secured via HTTPS and Basic Auth.</li>
            </ul>
          </v-card-text>
          <v-divider></v-divider>

          <v-card-title id="authentication">2. Authentication (API Auth Header)</v-card-title>
          <v-card-text>
            Merchanto will create a dedicated account for your organization and provide credentials via secure email.
          </v-card-text>
          <v-card-text>
            <ul>
              <li><b>Authentication method</b>: Basic Auth</li>
              <li>
                <b>Header format:</b>
                <br/>
                <code>Authorization: Basic &lt;Base64Encoded(username:password)></code>
              </li>
              <li>Merchanto will call your provided endpoints (Orders, Notifications) using these credentials.</li>
              <li>You must configure your API server to validate the Basic Auth header.
              </li>
            </ul>
          </v-card-text>
          <v-divider></v-divider>

          <v-card-title id="api-development">3. API Development Checklist</v-card-title>
          <v-card-text>
            Your technical team should complete the following tasks before testing:
          </v-card-text>
          <v-card-text>
            <ul>
              <li>
                <b>Define Available API Fields</b>
                <br/>
                Identify which fields you will provide.
                <br/>
                Prevent API Field Guide (<a href="/docs/prevent/visa-oi-lookup" target="_blank" rel="noopener" title="Visa OI Lookup">Visa OI Lookup</a>,
                <a href="/docs/prevent/mastercard-clarity-lookup" target="_blank" rel="noopener" title="MC Clarity Lookup">MC Clarity Lookup</a>,
                <a href="/docs/prevent/notifications" target="_blank" rel="noopener" title="Notifications">Notifications</a>).
              </li>
              <li>
                <b>Set Up API Server</b>
                <ol>
                  <li>Implement endpoints as described above.</li>
                  <li>Ensure HTTPS and Basic Auth are enforced.</li>
                </ol>
              </li>
              <li>
                <b>Performance Requirements</b>
                <br/>
                <ol>
                  <li>API response time must be &lt; <b>1000 ms</b>.</li>
                  <li>Responses must follow the documented status codes and formats.</li>
                </ol>
              </li>
              <li>
                <b>Error Handling</b>
                <br/>
                <ol>
                  <li>Return proper HTTP status codes.</li>
                  <li>Validate and sanitize inputs/outputs.</li>
                </ol>
              </li>
            </ul>
          </v-card-text>
          <v-divider></v-divider>

          <v-card-title id="json-response">4. Building the JSON Response</v-card-title>
          <v-card-text>Creating the JSON response is the most critical part of the Prevent API integration.</v-card-text>
          <v-card-text>
            <ul>
              <li>
                <b>Strict scheme requirements</b>
                <ul>
                  <li><a href="/docs/prevent/visa-oi-lookup" target="_blank" rel="noopener" title="Visa OI Lookup">Visa OI Lookups</a> and <a href="/docs/prevent/mastercard-clarity-lookup" target="_blank" rel="noopener" title="MC Clarity Lookup">MC Clarity Lookups</a> sections define which fields are <b>mandatory</b> and which are <b>optional</b></li>
                  <li>You must comply with their official specifications to ensure your responses are accepted.</li>
                </ul>
              </li>
              <li>
                <b>Provide as much data as possible &mdash; but only relevant data</b>
                <ul>
                  <li>Beyond mandatory fields, merchants should aim to include all optional fields they can reliably provide.</li>
                  <li>More details (e.g., product descriptions, customer service contacts, invoice references) increase the chance of deflecting disputes and lowering fraud claims.</li>
                  <li>
                    However, only provide <b>appropriate information for your business model</b>>. For example:
                    <ul>
                      <li>If you sell <b>physical goods</b> → include shipping details and delivery address.</li>
                      <li>If you sell <b>digital services or subscriptions</b> → do <b>not</b> include shipping information, as it may confuse issuers and cardholders.</li>
                    </ul>
                  </li>
                </ul>
              </li>
              <li>
                <b>Different response structures</b>
                <ul>
                  <li>Visa and Mastercard use different JSON formats.</li>
                  <li>Separate logic is required for Visa OI and Mastercard Clarity responses.</li>
                  <li>Examples are available in the <a href="https://dev-api.merchanto.org/api" target="_blank" rel="noopener" title="Swagger">Swagger</a> and at the <a href="/docs/prevent/visa-oi-lookup" target="_blank" rel="noopener" title="Visa OI Lookup">Visa OI Lookups</a> and <a href="/docs/prevent/mastercard-clarity-lookup" target="_blank" rel="noopener" title="MC Clarity Lookup">MC Clarity Lookups</a> sections.</li>
                </ul>
              </li>
              <li>
                <b>Performance requirement</b>
                <ul>
                  <li>Responses must be returned in <b>under 1000 ms</b>.</li>
                  <li>Invalid, incomplete, or late responses may result in disputes or lost liability protection.</li>
                </ul>
              </li>
              <li>
                <b>Validation & outcomes (how Merchanto handles your response)</b>
                <ul>
                  <li>Merchanto performs strict validation against scheme rules and our <i>Prevent API Field Guide</i>.</li>
                  <li>If <b>required fields are missing</b> or <b>data is invalid/misformatted</b>, the order is marked <b>Invalid</b> and <b>is not forwarded to the issuer</b>. From the scheme’s perspective, the case remains unanswered and will typically proceed to dispute.</li>
                  <li>If your response <b>arrives after the time window</b>, it is marked <b>Timeout</b>. In most cases, the issuer will not accept the response, and the case may proceed to dispute.</li>
                  <li>If you <b>cannot find the transaction</b>, return a compliant <b>Not Found</b> response. Be aware that absence of evidence often results in issuer escalation.</li>
                  <li>Valid, on-time responses are marked <b>Accepted</b> and forwarded to the issuer.</li>
                </ul>
              </li>
            </ul>
          </v-card-text>
          <v-card-text>
            Summary: Missing mandatory fields or invalid formats → <b>not forwarded (Invalid)</b> → high likelihood of dispute. Irrelevant (but otherwise valid) data won’t block forwarding, but it can reduce clarity and deflection effectiveness &mdash; so keep it relevant.
          </v-card-text>
          <v-divider></v-divider>

          <v-card-title id="testing">5. Testing</v-card-title>
          <v-card-text>
            Before going live, you must perform full integration testing to verify that your endpoints work as expected.
          </v-card-text>
          <v-card-text>
            Testing includes:
          </v-card-text>
          <v-card-text>
            <ul>
              <li>Validating request/response formats.</li>
              <li>Ensuring Basic Auth is correctly implemented.</li>
              <li>Measuring response times (&lt; 1000 ms).</li>
              <li>Confirming data accuracy and consistency.</li>
            </ul>
          </v-card-text>
          <v-card-text>Merchanto provides:</v-card-text>
          <v-card-text>
            <ul>
              <li>
                <b><a href="https://dev-api.merchanto.org/api" target="_blank" rel="noopener" title="Swagger">Swagger</a></b>, where you can:
                <ul>
                  <li>See example requests that Merchanto will send to your endpoints.</li>
                  <li>Review example response formats expected from your system.</li>
                  <li>Use validation endpoints to check response accuracy.</li>
                </ul>
              </li>
            </ul>
          </v-card-text>
          <v-card-text>Merchants will typically have <b>two scheduled meetings with Merchanto</b>:</v-card-text>
          <v-card-text>
            <ol>
              <li><b>Kick-off meeting</b> &mdash; before starting the integration.</li>
              <li><b>Sum-up meeting</b> &mdash; after successful enrollment in production.</li>
            </ol>
          </v-card-text>
          <v-card-text>During the integration process, all technical questions should be submitted to <a href="mailto:support@merchanto.org">support@merchanto.org</a>.</v-card-text>
          <v-divider></v-divider>

          <v-card-title id="monitoring-and-reporting">6. Monitoring & Reporting</v-card-title>
          <v-card-text>Once production enrollment is complete:</v-card-text>
          <v-card-text>
            <ul>
              <li>
                <b>Initial Monitoring</b>
                <br/>
                Merchanto will monitor your API responses for stability during the early production phase.
                <br/>
                Any issues identified will be flagged via email and must be resolved before the integration is considered stable.
              </li>
              <li>
                <b>Ongoing Monitoring</b>
                <br/>
                After stabilization, merchants are expected to monitor their own transactions, orders, and notifications directly in the <b>Merchanto platform</b>.
                <br/>
                All real-time statuses, history, and outcomes are visible there.
              </li>
              <li>
                <b>Reporting</b>
                <br/>
                At this stage, <b>Merchanto does not provide separate reports</b>.
                <br/>
                Merchants should use the platform’s dashboards and data export features for monitoring and reporting purposes.
              </li>
            </ul>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import {
  mdiCogOutline,
  mdiCheckboxBlankCircleOutline,
  mdiCellphoneCog,
  mdiChevronLeft,
} from '@mdi/js';
import useDocs from '@/views/docs/useDocs';

export default {
  setup() {
    const { scrollToElement } = useDocs();
    const summary = [
      { title: '1. Environment setup', link: 'environment-setup' },
      { title: '2. Authentication (API Auth Header)', link: 'authentication' },
      { title: '3. API Development Checklist', link: 'api-development' },
      { title: '4. Building the JSON Response', link: 'json-response' },
      { title: '5. Testing', link: 'testing' },
      { title: '6. Monitoring & Reporting', link: 'monitoring-and-reporting' },
    ];

    return {
      icons: {
        mdiCogOutline,
        mdiCheckboxBlankCircleOutline,
        mdiCellphoneCog,
        mdiChevronLeft,
      },
      summary,
      scrollToElement,
    };
  },
};
</script>

<style lang="scss">
@import '@core/preset/preset/pages/knowledge-base';
</style>
